import { Link, motion, Button, Badge, Check, X, ArrowRight, useAuth } from "@/pages/shared";
import { Helmet } from "react-helmet-async";
import { CLAIMS } from "@/config/claims";

const FREE_LABEL = `Free tier (${CLAIMS.freeAllowance.generations}/month + referrals)`;

// Competitor pricing & positioning last verified mid-2026 from public pricing pages.
const COMPETITORS = [
  {
    name: "Creatara",
    price: "£29",
    isUs: true,
    features: {
      "AI content generation": true,
      "Built-in affiliate tools": true,
      "Lead capture forms": true,
      "Monetization Score": true,
      "Revenue Campaigns wizard": true,
      "AI video (Sora 2, Pro plan)": true,
      "Brand voice training": true,
      "Multi-platform publishing": true,
      "Content calendar + scheduling": true,
      "API access (Enterprise)": true,
      [FREE_LABEL]: true,
      "Designed for solopreneurs": true,
    },
  },
  {
    name: "Jasper",
    price: "$69",
    features: {
      "AI content generation": true,
      "Built-in affiliate tools": false,
      "Lead capture forms": false,
      "Monetization Score": false,
      "Revenue Campaigns wizard": false,
      "AI video (Sora 2, Pro plan)": false,
      "Brand voice training": true,
      "Multi-platform publishing": "limited",
      "Content calendar + scheduling": "limited",
      "API access (Enterprise)": true,
      [FREE_LABEL]: "7-day trial",
      "Designed for solopreneurs": false,
    },
  },
  {
    name: "Copy.ai",
    price: "$29",
    features: {
      "AI content generation": true,
      "Built-in affiliate tools": false,
      "Lead capture forms": false,
      "Monetization Score": false,
      "Revenue Campaigns wizard": false,
      "AI video (Sora 2, Pro plan)": false,
      "Brand voice training": true,
      "Multi-platform publishing": false,
      "Content calendar + scheduling": false,
      "API access (Enterprise)": true,
      [FREE_LABEL]: "none — paid from $29",
      "Designed for solopreneurs": false,
    },
  },
  {
    name: "Writesonic",
    price: "$99",
    features: {
      "AI content generation": true,
      "Built-in affiliate tools": false,
      "Lead capture forms": false,
      "Monetization Score": false,
      "Revenue Campaigns wizard": false,
      "AI video (Sora 2, Pro plan)": false,
      "Brand voice training": "limited",
      "Multi-platform publishing": false,
      "Content calendar + scheduling": false,
      "API access (Enterprise)": true,
      [FREE_LABEL]: "7-day trial",
      "Designed for solopreneurs": false,
    },
  },
];

const Cell = ({ value }) => {
  if (value === true) return <Check className="w-5 h-5 text-teal-500 mx-auto" />;
  if (value === false) return <X className="w-5 h-5 text-violet-500/60 mx-auto" />;
  return <span className="text-xs text-muted-foreground">{value}</span>;
};

export default function ComparisonPage() {
  const { user } = useAuth();
  const ctaTo = user ? "/dashboard" : "/register";
  const ctaLabelTop = user ? "Go to Dashboard" : "Get Started Free";
  const ctaLabelBottom = user ? "Go to Dashboard" : "Start free";
  const featureKeys = Object.keys(COMPETITORS[0].features);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Helmet>
        <title>Creatara vs Jasper, Copy.ai, Writesonic — Solopreneur Comparison</title>
        <meta name="description" content="Side-by-side comparison of AI content platforms for solopreneurs: pricing, publishing, affiliate tools, lead capture and Monetization Score. Creatara from £0 (free plan) / £29 Pro." />
      </Helmet>

      <nav className="border-b border-white/5 sticky top-0 z-40 bg-background/80 backdrop-blur-md">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="text-xl font-bold">Creatara</Link>
          <Link to={ctaTo}><Button className="bg-violet-600 hover:bg-violet-700 rounded-full" data-testid="comparison-signup-btn">{ctaLabelTop}</Button></Link>
        </div>
      </nav>

      <section className="pt-20 pb-12 px-6 text-center">
        <Badge className="mb-4 bg-violet-600/10 text-violet-500 border-violet-600/20">Honest comparison</Badge>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4 gradient-text">Creatara vs Jasper, Copy.ai &amp; Writesonic</h1>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
          The other tools generate content. We help you turn it into <span className="text-violet-400 font-semibold">revenue</span>. Here's the side-by-side.
        </p>
      </section>

      <section className="px-6 pb-20">
        <div className="max-w-5xl mx-auto overflow-x-auto">
          <table className="w-full glass rounded-2xl overflow-hidden" data-testid="comparison-table">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left p-4 font-medium text-muted-foreground">Feature</th>
                {COMPETITORS.map((c) => (
                  <th key={c.name} className={`text-center p-4 ${c.isUs ? "bg-violet-600/10" : ""}`}>
                    <p className={`font-bold ${c.isUs ? "text-violet-400" : ""}`}>{c.name}</p>
                    <p className="text-xs text-muted-foreground">{c.price}/mo</p>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {featureKeys.map((feat) => (
                <tr key={feat} className="border-b border-white/5">
                  <td className="p-4 text-sm">{feat}</td>
                  {COMPETITORS.map((c) => (
                    <td key={c.name} className={`text-center p-4 ${c.isUs ? "bg-violet-600/5" : ""}`}>
                      <Cell value={c.features[feat]} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-xs text-muted-foreground mt-3 text-center" data-testid="comparison-verified-note">Competitor pricing and positioning last verified mid-2026 from each vendor's public pricing page. Entry-tier monthly prices shown. Spot something outdated? Email support@creatara.ai and we'll fix it.</p>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="glass rounded-xl p-6">
              <h3 className="text-lg font-bold mb-2 text-violet-400">Why solopreneurs pick us</h3>
              <p className="text-sm text-muted-foreground">We bake affiliate links, lead forms and a Monetization Score directly into the content workflow. Generate a post → attach a tracked link → see the clicks and leads it produces.</p>
            </div>
            <div className="glass rounded-xl p-6">
              <h3 className="text-lg font-bold mb-2">When to pick Jasper</h3>
              <p className="text-sm text-muted-foreground">If you're a marketing team writing for a polished brand with $69+/seat budget. Their enterprise templates and brand-governance tooling are unmatched at scale.</p>
            </div>
            <div className="glass rounded-xl p-6">
              <h3 className="text-lg font-bold mb-2">When to pick Copy.ai / Writesonic</h3>
              <p className="text-sm text-muted-foreground">Copy.ai now targets GTM/sales workflow automation; Writesonic has pivoted to AI-search visibility (GEO/SEO) tooling. Pick them for those jobs — pick Creatara if you want content creation, publishing and monetization in one workflow.</p>
            </div>
          </div>

          <div className="mt-16 text-center">
            <h2 className="text-3xl font-bold mb-4">{user ? "Ready to run your next campaign?" : "Try it in 60 seconds — no credit card"}</h2>
            <Link to={ctaTo}>
              <Button size="lg" className="bg-violet-600 hover:bg-violet-700 rounded-full px-8" data-testid="comparison-cta-btn">
                {ctaLabelBottom} <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            {!user && <p className="text-xs text-muted-foreground mt-3">{CLAIMS.freeAllowance.generations} generations / {CLAIMS.freeAllowance.period} + bonus credits for every referral. Pro starts at £29/mo.</p>}
          </div>
        </div>
      </section>
    </div>
  );
}
